import { useCallback, useRef } from 'react';

import { useUpdatePalletGroupMutation } from '../api/palletsApi';
import type { PalletGroupParams } from '../api/palletsApi';
import { toPalletGroup, type PalletConfig } from '../lib/pallet-config';

export interface UseUpdatePalletGroupOptions {
  /** Keep it stable (`useCallback`): `savePalletConfig` changes with it */
  onError?: (error: unknown) => void;
}

/** Every caller shares one mutation state, so the pallet view and the create dialog see each other's save */
const SAVE_KEY = 'pallet-group-save';

/**
 * Saves a whole pallet configuration of the consignment. A save replaces the
 * whole group with a configuration built from what the caller last saw, so two
 * saves must never overlap: one asked for while another runs is skipped, and
 * `isSaving` lets the UI block it. Resolves `true` once saved, `false` when
 * skipped or failed (after `onError`).
 */
export const useUpdatePalletGroup = ({ onError }: UseUpdatePalletGroupOptions = {}) => {
  const [updatePalletGroup, { isLoading: isSaving }] = useUpdatePalletGroupMutation({ fixedCacheKey: SAVE_KEY });
  const savingRef = useRef(false);

  const savePalletConfig = useCallback(
    async (params: PalletGroupParams, config: PalletConfig) => {
      if (savingRef.current || isSaving) return false;
      savingRef.current = true;
      try {
        await updatePalletGroup({ ...params, body: toPalletGroup(config) }).unwrap();
        return true;
      } catch (error) {
        onError?.(error);
        return false;
      } finally {
        savingRef.current = false;
      }
    },
    [updatePalletGroup, isSaving, onError]
  );

  return { savePalletConfig, isSaving };
};
